import { TimecardListItemData } from 'types/timecards';
import { t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import TimecardGroup, { GroupedTimecards } from './TimecardGroup';

type Props = {
    timecards: TimecardListItemData[];
    compact?: boolean;
    borderless?: boolean;
};

/** What a group is keyed on. Rows with an empty key are grouped under `''`. */
export type TimecardGroupKeyOf = (timecard: TimecardListItemData) => string;

/**
 * Groups timecards in the order they arrive, summing each group's hours and fees.
 *
 * Group ORDER follows the row order — first appearance of each key — but membership is by key, so a
 * key that reappears after another rejoins its first group rather than opening a second. Keeping a
 * key's rows in one group keeps its totals right; what it relies on is the caller sorting so that a
 * key's rows are contiguous, since the ribbon is drawn above a group's FIRST row and a later run
 * would otherwise render under the preceding group's ribbon. That is why every grouping sorts by its
 * own id and not only by the label it displays.
 *
 * Sums are over the rows passed in, so they are the group's own totals only where the caller holds all of
 * them. The paged list does not: it overrides them with `/v1/timecards/group-totals`, which sums the whole
 * filtered set.
 */
export const groupTimecards = (
    timecards: TimecardListItemData[],
    keyOf: TimecardGroupKeyOf
): Map<string, GroupedTimecards> => {
    const groupedTimecards: Map<string, GroupedTimecards> = new Map();
    timecards.forEach((timecard) => {
        const key = keyOf(timecard);
        let group = groupedTimecards.get(key);
        if (!group) {
            group = {
                billableTimeSumInHours: 0,
                loggedTimeSumInHours: 0,
                baseCurrency: '',
                totalAmountInBaseCurrency: 0,
                feesByCurrency: new Map(),
                timecards: [],
            };
            groupedTimecards.set(key, group);
        }
        // Non-billable rows contribute their full hours and zero fees.
        group.billableTimeSumInHours += timecard.timecardStats?.billedTimeInHours || 0;
        group.loggedTimeSumInHours += timecard.timecardStats?.totalTimeInHours || 0;
        group.totalAmountInBaseCurrency += timecard.isNonBillable
            ? 0
            : timecard.currencyDetails?.totalAmountInBaseCurrency || 0;
        group.baseCurrency = group.baseCurrency || timecard.currencyDetails?.baseCurrency || '';
        // Fees are also summed per ORIGINAL currency, because a group can hold timecards priced in
        // several and the ribbon lists one sum for each. Nothing is converted here — a currency only
        // gets a sum from rows already priced in it.
        const currency = timecard.timecardStats?.originalCurrencyTypeCode;
        const amountInOriginalCurrency = timecard.isNonBillable
            ? 0
            : timecard.timecardStats?.amountInOriginalCurrency || 0;
        if (currency && amountInOriginalCurrency) {
            group.feesByCurrency.set(currency, (group.feesByCurrency.get(currency) || 0) + amountInOriginalCurrency);
        }
        group.timecards.push(timecard);
    });

    return groupedTimecards;
};

export const groupTimecardsByDate = (timecards: TimecardListItemData[]): Map<string, GroupedTimecards> =>
    groupTimecards(timecards, (timecard) => timecard.timecardDate);

const TimecardList = ({ timecards, compact, borderless }: Props): JSX.Element => {
    const { classifiers } = useClassifiers({
        codes: ['TIMECARD_STATUS_TYPE'],
    });

    if (timecards.length === 0) {
        return (
            <div className="m-[14px] flex justify-center">
                <p className="text-body1">{t('timecards.no-timecards-found')}</p>
            </div>
        );
    }

    const groupedTimecards = groupTimecardsByDate(timecards);
    return (
        <div className="flex h-full flex-col">
            {Array.from(groupedTimecards).map(([date, group]) => (
                <TimecardGroup
                    key={date}
                    date={date}
                    timecardGroup={group}
                    classifiers={classifiers}
                    classifier={classifiers['TIMECARD_STATUS_TYPE']}
                    compact={compact}
                    borderless={borderless}
                />
            ))}
        </div>
    );
};

export default TimecardList;
